/**
 * A personagem feminina do campus, aplicada ao escritório v1.
 *
 * Gémeo do `officeChar.ts`, o mesmo truque de precedência: o `ensureCharTextures`
 * do `officeArt` começa cada frame com `if (scene.textures.exists(tex)) continue;`,
 * por isso quem registar a chave primeiro fica com ela. Este módulo corre
 * imediatamente antes e enche as chaves de ANDAR de cada visual feminino com a
 * arte pintada à mão da menina (quatro PNG, um por direcção); a boneca desenhada
 * em código passa a não-se-desenhar nessas chaves, sozinha, sem um `if` novo.
 *
 * O SENTADO também é arte, desde que ela chegou: quatro folhas próprias, uma por
 * direcção — incluindo as COSTAS, que o pacote masculino nunca teve (lá, o `up`
 * sentado continua derivado do boneco de pé). Até aí era a boneca desenhada em
 * código a fazer o papel, e fazia-o bem; a arte a sério é melhor.
 *
 * O que este módulo NÃO toca, de propósito:
 *   - `officeArt.ts` — nem uma linha; a boneca desenhada fica de reserva inteira
 *     e volta em todas as poses se algum PNG faltar (ver `femFalhadas`);
 *   - `officeAvatars.ts` — a marcha, a sombra, a profundidade e a etiqueta mexem
 *     nas mesmas chaves com os mesmos tamanhos.
 *
 * A passada é ARTE, não derivação: três ficheiros por direcção — o parado que já
 * existia e dois passos desenhados. Houve aqui, por umas horas, um balanço
 * derivado do frame parado (as pernas em pêndulo a partir da anca); chegaram as
 * folhas verdadeiras e essas vinte linhas foram apagadas, como estava previsto.
 *
 * Os ficheiros dos passos foram normalizados para esta caixa antes de entrarem —
 * vinham desenhados grandes e cada um com o seu enquadramento. O que a
 * normalização garante, e que é o que impede o boneco de tremer entre frames:
 * pés na linha 47, topo na 2, e os ombros à mesma largura da pose parada.
 */

import type { Facing } from "@greenvoice/shared";
import type PhaserT from "phaser";

import { frameKey, marcarSentadoDeArte, sitKey } from "./officeArt";

/** O canvas de cada frame, igual ao do resto do escritório: origem no fundo = pés. */
const LARG = 32;
const ALT = 48;

/** Três frames por direcção, como o `ensureCharTextures` espera (0 esq · 1 parado · 2 dir). */
const PASSOS = 3;

const DIRS: readonly Facing[] = ["down", "left", "right", "up"];

/**
 * A arte da menina, um PNG por direcção, já composta em 32×48 com os pés na sola
 * e a altura normalizada à do boneco (o bake offline fez o recorte-à-caixa e a
 * escala; ver os `char-fem-*` do campus). O mapa segue o mundo: `down` é a frente,
 * `up` são as costas.
 */
const FEMININA: Record<Facing, readonly { fonte: string; ficheiro: string }[]> = {
  down: folhas("down"),
  left: folhas("left"),
  right: folhas("right"),
  up: folhas("up"),
};

/**
 * A pose sentada de cada direcção.
 *
 * Normalizada para a MESMA caixa da do rapaz — figura entre as linhas 6 e 47 —
 * porque é a mesma cadeira: a almofada alinha-se pela anca do sprite
 * (`seatDropFor`), e uma figura mais alta ou mais baixa flutuava ou afundava no
 * assento. Os ombros ficaram à largura dos dela DE PÉ, para não encolher ao
 * sentar-se.
 */
const SENTADA: Record<Facing, { fonte: string; ficheiro: string }> = {
  down: { fonte: "char:src:fem:sit:down", ficheiro: "/campus/char-fem-sit-down-v1.png" },
  left: { fonte: "char:src:fem:sit:left", ficheiro: "/campus/char-fem-sit-left-v1.png" },
  right: { fonte: "char:src:fem:sit:right", ficheiro: "/campus/char-fem-sit-right-v1.png" },
  up: { fonte: "char:src:fem:sit:up", ficheiro: "/campus/char-fem-sit-up-v1.png" },
};

/**
 * As três folhas de uma direcção, na ordem dos frames: passo, parada, passo.
 *
 * A do meio é a que já cá estava (`char-fem-<dir>-v1`); as das pontas são novas
 * (`-step0`, `-step2`). A ordem importa — é o índice do frame, e o
 * `ensureCharTextures` conta com 0 = pé esquerdo, 1 = parado, 2 = pé direito.
 */
function folhas(dir: Facing): readonly { fonte: string; ficheiro: string }[] {
  return [
    { fonte: `char:src:fem:${dir}:0`, ficheiro: `/campus/char-fem-${dir}-step0-v1.png` },
    { fonte: `char:src:fem:${dir}`, ficheiro: `/campus/char-fem-${dir}-v1.png` },
    { fonte: `char:src:fem:${dir}:2`, ficheiro: `/campus/char-fem-${dir}-step2-v1.png` },
  ];
}

/**
 * Fontes femininas que falharam o carregamento, por cena.
 *
 * A armadilha de sempre: um 404 no `load.image` não dá erro — o Phaser mete a
 * textura `__MISSING` (quadrado preto) e `exists` mente. Uma direcção aqui
 * marcada é saltada e a boneca desenhada em código volta ao serviço nessa
 * direcção. `WeakMap` e não um `Set` global porque cada montagem da página cria
 * uma cena nova; o registo morre com ela.
 */
const femFalhadas = new WeakMap<PhaserT.Scene, Set<string>>();

/**
 * Põe os quatro PNG na fila de carregamento e arma a rede de segurança.
 *
 * Tem de ser chamado no `preload` da cena, a par do `preloadOfficeChar`: o
 * `create` só corre depois disto, e é essa garantia que deixa o
 * `ensureOfficeCharFemale` assumir que cada folha ou está pronta ou está marcada.
 */
export function preloadOfficeCharFemale(scene: PhaserT.Scene): void {
  const semArte = new Set<string>();
  femFalhadas.set(scene, semArte);
  scene.load.on("loaderror", (file: { key: string }) => {
    if (file.key.startsWith("char:src:fem:")) semArte.add(file.key);
  });
  for (const trio of Object.values(FEMININA)) {
    for (const f of trio) scene.load.image(f.fonte, f.ficheiro);
  }
  for (const f of Object.values(SENTADA)) scene.load.image(f.fonte, f.ficheiro);
}

function tela(): { canvas: HTMLCanvasElement; ctx: CanvasRenderingContext2D } | null {
  const canvas = document.createElement("canvas");
  canvas.width = LARG;
  canvas.height = ALT;
  const ctx = canvas.getContext("2d");
  if (!ctx) return null;
  ctx.imageSmoothingEnabled = false; // já em 32×48: cópia 1:1, nada a interpolar
  return { canvas, ctx };
}

/**
 * Regista os 12 frames de andar (4 direcções × 3 passos) de um visual feminino
 * com a arte real.
 *
 * Recebe a chave e não o `CharSprite` inteiro porque a arte é uma pessoa só — as
 * cores da boneca desenhada deixam de contar de pé (o sentado, esse, ainda as
 * usa). Continua a ser chamado por avatar, mas o guarda `exists` faz da segunda
 * chamada em diante uma passagem por 12 `if`. As chaves sentadas ficam de fora,
 * para o `ensureCharTextures` as desenhar com o cabelo comprido.
 */
export function ensureOfficeCharFemale(scene: PhaserT.Scene, key: string): void {
  const semArte = femFalhadas.get(scene);
  for (const dir of DIRS) {
    const trio = FEMININA[dir];
    for (let fi = 0; fi < PASSOS; fi += 1) {
      const f = trio[fi];
      if (!f) continue;
      // Cada frame cai por si: se faltar SÓ a folha de um passo, esse frame
      // fica com a boneca desenhada e os outros dois mantêm a arte. É a mesma
      // regra que o `officeChar.ts` usa para as poses sentadas, e é o que
      // impede um 404 numa folha de deitar abaixo a direcção inteira.
      if (semArte?.has(f.fonte)) continue; // 404: cai na boneca desenhada
      if (!scene.textures.exists(f.fonte)) continue; // nem chegou a carregar
      const tex = frameKey(key, dir, fi);
      if (scene.textures.exists(tex)) continue;
      const t = tela();
      if (!t) continue;
      t.ctx.drawImage(scene.textures.get(f.fonte).getSourceImage() as HTMLImageElement, 0, 0);
      scene.textures.addCanvas(tex, t.canvas);
    }
  }

  // As poses sentadas, pelo mesmo caminho e com a mesma regra de precedência: a
  // que for reclamada aqui deixa de ser desenhada pelo `ensureCharTextures`.
  // Cada direcção cai por si — sem a folha, volta a boneca desenhada, que
  // continua a saber fazer as quatro.
  for (const dir of DIRS) {
    const f = SENTADA[dir];
    if (semArte?.has(f.fonte)) continue;
    if (!scene.textures.exists(f.fonte)) continue;
    const tex = sitKey(key, dir);
    if (scene.textures.exists(tex)) continue;
    const t = tela();
    if (!t) continue;
    t.ctx.drawImage(scene.textures.get(f.fonte).getSourceImage() as HTMLImageElement, 0, 0);
    scene.textures.addCanvas(tex, t.canvas);
    marcarSentadoDeArte(tex);
  }
}
